import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GOOGLE4_MEASUREMENT_ID } from '@/config';
import AcademyDashboard from '@/pages/AcademyDashboard';
import CandidatePage from '@/pages/Candidate';
import CandidateInfoPage from '@/pages/CandidateInfo';
import CandidateInterviewsPage from '@/pages/CandidateInterviews';
import CandidateJobs from '@/pages/CandidateJobs';
import CandidateNotesPage from '@/pages/CandidateNotes';
import CandidatePreScreeningInterview from '@/pages/CandidatePreScreeningInterview';
import CandidatesBoard from '@/pages/CandidatesBoard';
import CandidatesList from '@/pages/CandidatesList';
import CandidateTechnicalValidationInterview from '@/pages/CandidateTechnicalValidationInterview';
import CEOxColdEmail from '@/pages/CEOxColdEmail';
import CEOxDashboard from '@/pages/CEOxDashboard';
import CEOxGoogleAds from '@/pages/CEOxGoogleAds';
import CEOxPipeline from '@/pages/CEOxPipeline';
import CEOxSEO from '@/pages/CEOxSEO';
import CEOxSettings from '@/pages/CEOxSettings';
import CEOxStackSetup from '@/pages/CEOxStackSetup';
import ChallengeManagementPage from '@/pages/ChallengeManagement';
import ChallengeReviewersPage from '@/pages/ChallengeReviewers';
import CheckinDetailsPage from '@/pages/CheckinDetails';
import CheckinsReportPage from '@/pages/CheckinsReport';
import CheckinsUnreviewed from '@/pages/CheckinsUnreviewed';
import Dashboard from '@/pages/Dashboard';
import DbErd from '@/pages/DbErd';
import FinanceXBankAccounts from '@/pages/FinanceXBankAccounts';
import FinanceXClients from '@/pages/FinanceXClients';
import FinanceXContractors from '@/pages/FinanceXContractors';
import FinanceXDashboard from '@/pages/FinanceXDashboard';
import FinanceXImport from '@/pages/FinanceXImport';
import FinanceXInvoiceDetail from '@/pages/FinanceXInvoiceDetail';
import FinanceXInvoices from '@/pages/FinanceXInvoices';
import FinanceXPayroll from '@/pages/FinanceXPayroll';
import FinanceXPayrollReport from '@/pages/FinanceXPayrollReport';
import FinanceXPnL from '@/pages/FinanceXPnL';
import FinanceXReviewQueue from '@/pages/FinanceXReviewQueue';
import FinanceXRules from '@/pages/FinanceXRules';
import FinanceXSubscriptionDetail from '@/pages/FinanceXSubscriptionDetail';
import FinanceXSubscriptions from '@/pages/FinanceXSubscriptions';
import FinanceXTransactions from '@/pages/FinanceXTransactions';
import FinanceXVendorDetail from '@/pages/FinanceXVendorDetail';
import FinanceXVendors from '@/pages/FinanceXVendors';
import HiringReports from '@/pages/HiringReports';
import HomePage from '@/pages/Home';
import HomeAttachments from '@/pages/HomeAttachments';
import HomePersonalInfo from '@/pages/HomePersonalInfo';
import Integrations from '@/pages/Integrations';
import JobPage from '@/pages/Job';
import JobCandidates from '@/pages/JobCandidates';
import JobDetails from '@/pages/JobDetails';
import JobNotes from '@/pages/JobNotes';
import JobsList from '@/pages/JobsList';
import ManageRotatingQuestionPage from '@/pages/ManageRotatingQuestion';
import NotAllowedPage from '@/pages/NotAllowedPage';
import NotFoundPage from '@/pages/NotFoundPage';
import NotInvitedPage from '@/pages/NotInvitedPage';
import OnboardingPage from '@/pages/Onboarding';
import PeopleXDashboard from '@/pages/PeopleXDashboard';
import PermissionsManagement from '@/pages/PermissionsManagement';
import ProfilePage from '@/pages/Profile';
import PTORequestsPage from '@/pages/PTORequests';
import ResetPasswordPage from '@/pages/ResetPassword';
import RotatingQuestionsListPage from '@/pages/RotatingQuestionsList';
import SeminarDetails from '@/pages/SeminarDetails';
import SeminarsListPage from '@/pages/SeminarsList';
import SeoAuditDetail from '@/pages/SeoAuditDetail';
import SeoAuditList from '@/pages/SeoAuditList';
import SeoAutomation from '@/pages/SeoAutomation';
import SeoChangelog from '@/pages/SeoChangelog';
import SeoContentDetail from '@/pages/SeoContentDetail';
import SeoContentList from '@/pages/SeoContentList';
import SeoCopilot from '@/pages/SeoCopilot';
import SeoInternalLinks from '@/pages/SeoInternalLinks';
import SeoKeywordDetail from '@/pages/SeoKeywordDetail';
import SeoKeywordList from '@/pages/SeoKeywordList';
import SeoPageDetail from '@/pages/SeoPageDetail';
import SeoPageList from '@/pages/SeoPageList';
import SeoReports from '@/pages/SeoReports';
import SeoTaskList from '@/pages/SeoTaskList';
import SEOxProject from '@/pages/SEOxProject';
import SEOxProjectList from '@/pages/SEOxProjectList';
import SEOxProjectOverview from '@/pages/SEOxProjectOverview';
import SEOxProjectSettings from '@/pages/SEOxProjectSettings';
import SignInPage from '@/pages/SignIn';
import Staff from '@/pages/Staff';
import StaffMemberAttachments from '@/pages/StaffMemberAttachments';
import StaffMemberInfo from '@/pages/StaffMemberInfo';
import StaffMemberJob from '@/pages/StaffMemberJob';
import StaffMemberNotes from '@/pages/StaffMemberNotes';
import StaffMemberProfessionalBackground from '@/pages/StaffMemberProfessionalBackground';
import StaffMemberResume from '@/pages/StaffMemberResume';
import StaffMemberTimeOff from '@/pages/StaffMemberTimeOff';
import TalentPoolList from '@/pages/TalentPoolList';
import TalentXDashboard from '@/pages/TalentXDashboard';
import Teams from '@/pages/Teams';
import TrendsReportPage from '@/pages/TrendsReport';
import TrendsReportDetailPage from '@/pages/TrendsReportDetail';
import UserCheckinsPage from '@/pages/UserCheckins';
import UsersPage from '@/pages/Users';
import UsersManagement from '@/pages/UsersManagement';
import { Paths } from '@/types/routes';
import CheckinManager from './checkinManager';
import LegacyRedirect from './LegacyRedirects';
import RouteHandler from './RouteHandler';

const AppRoutes = function (): JSX.Element {
  useEffect(() => {
    if (GOOGLE4_MEASUREMENT_ID) {
      ReactGA.initialize(GOOGLE4_MEASUREMENT_ID);
      ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }
  }, []);

  return (
    <Routes>
      <Route path={Paths.NOT_INVITED} element={<NotInvitedPage />} />
      <Route path={Paths.NOT_ALLOWED} element={<NotAllowedPage />} />
      <Route path={Paths.ONBOARDING} element={<OnboardingPage />} />
      <Route path={Paths.LOGIN} element={<SignInPage />} />
      <Route path={Paths.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route path={Paths.HOME} element={<RouteHandler withLoading element={<HomePage />} />}>
        <Route
          index
          path={Paths.HOME}
          element={<RouteHandler nestedRoute element={<Dashboard />} />}
        />
        <Route
          path={Paths.HOME_PERSONAL_INFO}
          element={<RouteHandler nestedRoute element={<HomePersonalInfo />} />}
        />
        <Route
          path={Paths.HOME_ATTACHMENTS}
          element={<RouteHandler nestedRoute element={<HomeAttachments />} />}
        />
      </Route>
      <Route path={Paths.PROFILE} element={<RouteHandler element={<ProfilePage />} />} />

      {/* Service landing pages */}
      <Route path="/talentx" element={<RouteHandler element={<TalentXDashboard />} />} />
      <Route path="/peoplex" element={<RouteHandler element={<PeopleXDashboard />} />} />
      <Route path="/academy" element={<RouteHandler element={<AcademyDashboard />} />} />

      {/* PeopleX routes (formerly /people/*) */}
      <Route path={Paths.USERS} element={<RouteHandler element={<UsersPage />} />} />
      <Route path={Paths.USER_CHECKINS} element={<RouteHandler element={<UserCheckinsPage />} />} />
      <Route
        path={Paths.USER_CHECKIN_DETAILS}
        element={<RouteHandler element={<CheckinDetailsPage />} />}
      />
      <Route
        path={Paths.MY_CHECKIN_DETAILS}
        element={<RouteHandler element={<CheckinManager />} />}
      />
      <Route path={Paths.MY_CHECKINS} element={<RouteHandler element={<UserCheckinsPage />} />} />
      <Route
        path={Paths.ROTATING_QUESTIONS}
        element={<RouteHandler element={<RotatingQuestionsListPage />} />}
      />
      <Route
        path={Paths.MANAGE_ROTATING_QUESTION}
        element={<RouteHandler element={<ManageRotatingQuestionPage />} />}
      />
      <Route path={Paths.TRENDS_REPORT} element={<RouteHandler element={<TrendsReportPage />} />} />
      <Route
        path={Paths.TRENDS_REPORT_DETAIL}
        element={<RouteHandler element={<TrendsReportDetailPage />} />}
      />
      <Route path={Paths.TEAMS} element={<RouteHandler element={<Teams />} />} />
      <Route
        path={Paths.CHECKINS_REPORT}
        element={<RouteHandler element={<CheckinsReportPage />} />}
      />
      <Route
        path={Paths.CHECKINS_UNREVIEWED}
        element={<RouteHandler element={<CheckinsUnreviewed />} />}
      />
      <Route path={Paths.PTO_REQUESTS} element={<RouteHandler element={<PTORequestsPage />} />} />
      <Route path={Paths.STAFF} element={<RouteHandler element={<Staff />} />} />
      <Route
        path={Paths.STAFF_MEMBER_INFO}
        element={<RouteHandler element={<StaffMemberInfo />} />}
      >
        <Route
          index
          path={Paths.STAFF_MEMBER_INFO}
          element={<RouteHandler nestedRoute element={<StaffMemberResume />} />}
        />
        <Route
          path={Paths.STAFF_MEMBER_JOB}
          element={<RouteHandler nestedRoute element={<StaffMemberJob />} />}
        />
        <Route
          path={Paths.STAFF_MEMBER_ATTACHMENTS}
          element={<RouteHandler nestedRoute element={<StaffMemberAttachments />} />}
        />
        <Route
          path={Paths.STAFF_MEMBER_TIME_OFF}
          element={<RouteHandler nestedRoute element={<StaffMemberTimeOff />} />}
        />
        <Route
          path={Paths.STAFF_MEMBER_NOTES}
          element={<RouteHandler nestedRoute element={<StaffMemberNotes />} />}
        />
        <Route
          path={Paths.STAFF_MEMBER_BACKGROUND}
          element={<RouteHandler nestedRoute element={<StaffMemberProfessionalBackground />} />}
        />
      </Route>

      {/* TalentX routes (formerly /hiring/*) */}
      <Route path={Paths.CANDIDATES} element={<RouteHandler element={<CandidatesList />} />} />
      <Route path={Paths.TALENT_POOL} element={<RouteHandler element={<TalentPoolList />} />} />
      <Route
        path={Paths.CHALLENGES}
        element={<RouteHandler element={<ChallengeManagementPage />} />}
      />
      <Route
        path={Paths.CHALLENGES_REVIEWERS}
        element={<RouteHandler element={<ChallengeReviewersPage />} />}
      />
      <Route path={Paths.REPORTS} element={<RouteHandler element={<HiringReports />} />} />
      <Route path={Paths.JOBS} element={<RouteHandler element={<JobsList />} />} />
      <Route
        path={Paths.CANDIDATES_BOARD}
        element={<RouteHandler element={<CandidatesBoard />} />}
      />
      <Route path={Paths.CANDIDATE_DETAILS} element={<RouteHandler element={<CandidatePage />} />}>
        <Route
          index
          path={Paths.CANDIDATE_DETAILS}
          element={<RouteHandler nestedRoute element={<CandidateInfoPage />} />}
        />
        <Route
          path={Paths.CANDIDATE_NOTES}
          element={<RouteHandler nestedRoute element={<CandidateNotesPage />} />}
        />
        <Route
          path={Paths.CANDIDATE_INTERVIEWS}
          element={<RouteHandler nestedRoute element={<CandidateInterviewsPage />} />}
        />
        <Route
          path={Paths.CANDIDATE_JOBS}
          element={<RouteHandler nestedRoute element={<CandidateJobs />} />}
        />
      </Route>
      <Route
        path={Paths.TALENT_POOL_BOARD}
        element={<RouteHandler element={<CandidatesBoard />} />}
      />
      <Route
        path={Paths.TALENT_POOL_DETAILS}
        element={<RouteHandler element={<CandidatePage isTalentPool />} />}
      >
        <Route
          index
          path={Paths.TALENT_POOL_DETAILS}
          element={<RouteHandler nestedRoute element={<CandidateInfoPage />} />}
        />
        <Route
          path={Paths.TALENT_POOL_INTERVIEWS}
          element={<RouteHandler nestedRoute element={<CandidateInterviewsPage />} />}
        />
        <Route
          path={Paths.TALENT_POOL_NOTES}
          element={<RouteHandler nestedRoute element={<CandidateNotesPage />} />}
        />
        <Route
          path={Paths.TALENT_POOL_JOBS}
          element={<RouteHandler nestedRoute element={<CandidateJobs />} />}
        />
      </Route>
      <Route path={Paths.JOB_DETAILS} element={<RouteHandler element={<JobPage />} />}>
        <Route
          index
          path={Paths.JOB_DETAILS}
          element={<RouteHandler nestedRoute element={<JobDetails />} />}
        />
        <Route
          path={Paths.JOB_CANDIDATES}
          element={<RouteHandler nestedRoute element={<JobCandidates />} />}
        />
        <Route
          path={Paths.JOB_NOTES}
          element={<RouteHandler nestedRoute element={<JobNotes />} />}
        />
      </Route>

      {/* SEOx routes */}
      <Route path={Paths.SEOX_PROJECTS} element={<RouteHandler element={<SEOxProjectList />} />} />
      <Route
        path={Paths.SEOX_PROJECT_OVERVIEW}
        element={<RouteHandler element={<SEOxProject />} />}
      >
        <Route index element={<RouteHandler nestedRoute element={<SEOxProjectOverview />} />} />
        <Route path="audits" element={<RouteHandler nestedRoute element={<SeoAuditList />} />} />
        <Route
          path="audits/:auditId"
          element={<RouteHandler nestedRoute element={<SeoAuditDetail />} />}
        />
        <Route
          path="keywords"
          element={<RouteHandler nestedRoute element={<SeoKeywordList />} />}
        />
        <Route
          path="keywords/:keywordId"
          element={<RouteHandler nestedRoute element={<SeoKeywordDetail />} />}
        />
        <Route path="pages" element={<RouteHandler nestedRoute element={<SeoPageList />} />} />
        <Route
          path="pages/:pageId"
          element={<RouteHandler nestedRoute element={<SeoPageDetail />} />}
        />
        <Route path="links" element={<RouteHandler nestedRoute element={<SeoInternalLinks />} />} />
        <Route path="copilot" element={<RouteHandler nestedRoute element={<SeoCopilot />} />} />
        <Route path="tasks" element={<RouteHandler nestedRoute element={<SeoTaskList />} />} />
        <Route path="content" element={<RouteHandler nestedRoute element={<SeoContentList />} />} />
        <Route
          path="content/:analysisId"
          element={<RouteHandler nestedRoute element={<SeoContentDetail />} />}
        />
        <Route
          path="automation"
          element={<RouteHandler nestedRoute element={<SeoAutomation />} />}
        />
        <Route path="changelog" element={<RouteHandler nestedRoute element={<SeoChangelog />} />} />
        <Route path="reports" element={<RouteHandler nestedRoute element={<SeoReports />} />} />
        <Route
          path="settings"
          element={<RouteHandler nestedRoute element={<SEOxProjectSettings />} />}
        />
      </Route>

      {/* FinanceX routes */}
      <Route path="/financex" element={<RouteHandler element={<FinanceXDashboard />} />} />
      <Route
        path={Paths.FINANCEX_CLIENTS}
        element={<RouteHandler element={<FinanceXClients />} />}
      />
      <Route
        path={Paths.FINANCEX_VENDORS}
        element={<RouteHandler element={<FinanceXVendors />} />}
      />
      <Route
        path={Paths.FINANCEX_VENDOR_DETAIL}
        element={<RouteHandler element={<FinanceXVendorDetail />} />}
      />
      <Route
        path={Paths.FINANCEX_SUBSCRIPTIONS}
        element={<RouteHandler element={<FinanceXSubscriptions />} />}
      />
      <Route
        path={Paths.FINANCEX_SUBSCRIPTION_DETAIL}
        element={<RouteHandler element={<FinanceXSubscriptionDetail />} />}
      />
      <Route
        path={Paths.FINANCEX_INVOICES}
        element={<RouteHandler element={<FinanceXInvoices />} />}
      />
      <Route
        path={Paths.FINANCEX_INVOICE_DETAIL}
        element={<RouteHandler element={<FinanceXInvoiceDetail />} />}
      />
      <Route
        path={Paths.FINANCEX_TRANSACTIONS}
        element={<RouteHandler element={<FinanceXTransactions />} />}
      />
      <Route
        path={Paths.FINANCEX_REVIEW}
        element={<RouteHandler element={<FinanceXReviewQueue />} />}
      />
      <Route path={Paths.FINANCEX_IMPORT} element={<RouteHandler element={<FinanceXImport />} />} />
      <Route path={Paths.FINANCEX_RULES} element={<RouteHandler element={<FinanceXRules />} />} />
      <Route path={Paths.FINANCEX_PNL} element={<RouteHandler element={<FinanceXPnL />} />} />
      <Route
        path={Paths.FINANCEX_CONTRACTORS}
        element={<RouteHandler element={<FinanceXContractors />} />}
      />
      <Route
        path={Paths.FINANCEX_PAYROLL}
        element={<RouteHandler element={<FinanceXPayroll />} />}
      />
      <Route
        path={Paths.FINANCEX_PAYROLL_REPORT}
        element={<RouteHandler element={<FinanceXPayrollReport />} />}
      />
      <Route
        path={Paths.FINANCEX_BANK_ACCOUNTS}
        element={<RouteHandler element={<FinanceXBankAccounts />} />}
      />

      {/* Dev tools */}
      <Route path="/dev/db-erd" element={<RouteHandler element={<DbErd />} />} />

      {/* Legacy SEOx redirects: old flat paths + old /growthx base */}
      <Route path="/seox/audits" element={<Navigate to="/seox" replace />} />
      <Route path="/seox/keywords" element={<Navigate to="/seox" replace />} />
      <Route path="/seox/content" element={<Navigate to="/seox" replace />} />
      <Route path="/seox/reports" element={<Navigate to="/seox" replace />} />
      <Route path="/seox/settings" element={<Navigate to="/seox" replace />} />
      <Route path="/growthx/*" element={<Navigate to="/seox" replace />} />

      {/* Academy routes */}
      <Route path={Paths.SEMINARS} element={<RouteHandler element={<SeminarsListPage />} />} />
      <Route
        path={Paths.SEMINARS_DETAILS}
        element={<RouteHandler element={<SeminarDetails />} />}
      />

      {/* Interview routes (public, no prefix change) */}
      <Route
        path={Paths.CANDIDATE_PRE_SCREENING_INTERVIEW}
        element={<CandidatePreScreeningInterview />}
      />
      <Route
        path={Paths.CANDIDATE_TECHNICAL_VALIDATION_INTERVIEW}
        element={<CandidateTechnicalValidationInterview />}
      />

      {/* CEOx */}
      <Route
        path={Paths.CEOX_STACK_SETUP}
        element={<RouteHandler element={<CEOxStackSetup />} />}
      />
      <Route path={Paths.CEOX_DASHBOARD} element={<RouteHandler element={<CEOxDashboard />} />} />
      <Route path={Paths.CEOX_PIPELINE} element={<RouteHandler element={<CEOxPipeline />} />} />
      <Route path={Paths.CEOX_COLD_EMAIL} element={<RouteHandler element={<CEOxColdEmail />} />} />
      <Route path={Paths.CEOX_SEO} element={<RouteHandler element={<CEOxSEO />} />} />
      <Route path={Paths.CEOX_GOOGLE_ADS} element={<RouteHandler element={<CEOxGoogleAds />} />} />
      <Route path={Paths.CEOX_SETTINGS} element={<RouteHandler element={<CEOxSettings />} />} />

      {/* Settings */}
      <Route path={Paths.SETTINGS_USERS} element={<RouteHandler element={<UsersManagement />} />} />
      <Route
        path={Paths.SETTINGS_PERMISSIONS}
        element={<RouteHandler element={<PermissionsManagement />} />}
      />
      <Route
        path={Paths.SETTINGS_INTEGRATIONS}
        element={<RouteHandler element={<Integrations />} />}
      />

      {/* Legacy redirects: /hiring/* → /talentx/*, /people/* → /peoplex/* */}
      <Route path="/hiring/*" element={<LegacyRedirect />} />
      <Route path="/people/*" element={<LegacyRedirect />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
